// ============================================
// STAFF PRESENCE MODULE
// Module de présence en temps réel du personnel
// Pour Dashboard Admin/Manager
// ============================================

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
    Users, 
    CheckCircle2, 
    Clock, 
    XCircle, 
    AlertTriangle,
    RefreshCw,
    Building2,
    ChefHat,
    Wine,
    Briefcase,
    Filter
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';

// Couleurs par service
const SERVICE_COLORS = {
    'Salle': { bg: 'bg-blue-100', text: 'text-blue-700', icon: Users },
    'Cuisine': { bg: 'bg-orange-100', text: 'text-orange-700', icon: ChefHat },
    'Bar': { bg: 'bg-purple-100', text: 'text-purple-700', icon: Wine },
    'Direction': { bg: 'bg-slate-100', text: 'text-slate-700', icon: Briefcase },
    'default': { bg: 'bg-gray-100', text: 'text-gray-700', icon: Building2 }
};

// Labels de statut
const STATUS_CONFIG = {
    'PRESENT': { label: 'Présent', color: 'bg-green-500', textColor: 'text-green-700', bgLight: 'bg-green-50' },
    'LATE': { label: 'En retard', color: 'bg-amber-500', textColor: 'text-amber-700', bgLight: 'bg-amber-50' },
    'PENDING': { label: 'En attente', color: 'bg-gray-400', textColor: 'text-gray-600', bgLight: 'bg-gray-50' },
    'ABSENT': { label: 'Absent', color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-50' },
    'AUTO_ABSENT': { label: 'Absent (auto)', color: 'bg-red-400', textColor: 'text-red-600', bgLight: 'bg-red-50' },
    'NOT_CLOCKED': { label: 'Non pointé', color: 'bg-orange-400', textColor: 'text-orange-600', bgLight: 'bg-orange-50' }
};

const StaffPresenceModule = ({ compact = false }) => {
    const { api } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [serviceFilter, setServiceFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    const fetchPresenceData = useCallback(async () => {
        try {
            setLoading(true);
            const today = new Date().toISOString().split('T')[0];
            
            // Récupérer les présences du jour
            const res = await api().get(`/attendance?date=${today}`);
            const attendances = res.data || [];
            
            // Récupérer les schedules du jour pour avoir le total prévu
            const schedRes = await api().get(`/schedules?date_from=${today}&date_to=${today}`);
            const schedules = schedRes.data || [];
            
            // Calculer les statistiques
            const stats = {
                total_scheduled: schedules.length,
                present: 0,
                late: 0,
                absent: 0,
                pending: 0,
                by_service: {}
            };
            
            // Traiter les présences avec les schedules
            const presenceList = schedules.map(schedule => {
                const attendance = attendances.find(a => a.schedule_id === schedule.id);
                const status = attendance?.status || 'PENDING';
                const service = schedule.staff_service || 'Autre';
                
                // Comptage
                if (status === 'PRESENT') stats.present++;
                else if (status === 'LATE') stats.late++;
                else if (status === 'ABSENT' || status === 'AUTO_ABSENT') stats.absent++;
                else stats.pending++;
                
                // Par service
                if (!stats.by_service[service]) {
                    stats.by_service[service] = { present: 0, late: 0, absent: 0, pending: 0, total: 0 };
                }
                stats.by_service[service].total++;
                if (status === 'PRESENT') stats.by_service[service].present++;
                else if (status === 'LATE') stats.by_service[service].late++;
                else if (status === 'ABSENT' || status === 'AUTO_ABSENT') stats.by_service[service].absent++;
                else stats.by_service[service].pending++;
                
                return {
                    id: schedule.id,
                    staff_id: schedule.staff_id,
                    staff_name: schedule.staff_name,
                    service: service,
                    position: schedule.staff_position || '',
                    start_time: schedule.start_time,
                    end_time: schedule.end_time,
                    status: status,
                    check_in_time: attendance?.check_in_time,
                    check_out_time: attendance?.check_out_time,
                    late_minutes: attendance?.late_minutes || 0,
                    is_closed: attendance?.is_closed || false,
                    auto_clock_out: attendance?.auto_clock_out || false
                };
            });
            
            setData({
                stats,
                presence_list: presenceList,
                services: Object.keys(stats.by_service)
            });
        } catch (error) {
            console.error('Error fetching presence data:', error);
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        fetchPresenceData();
        // Rafraîchir toutes les 30 secondes
        const interval = setInterval(fetchPresenceData, 30000);
        return () => clearInterval(interval);
    }, [fetchPresenceData]);

    // Filtrer la liste
    const filteredList = data?.presence_list?.filter(item => {
        if (serviceFilter !== 'all' && item.service !== serviceFilter) return false;
        if (statusFilter !== 'all' && item.status !== statusFilter) return false;
        return true;
    }) || [];

    const getServiceConfig = (service) => SERVICE_COLORS[service] || SERVICE_COLORS['default'];
    const getStatusConfig = (status) => STATUS_CONFIG[status] || STATUS_CONFIG['PENDING'];

    const formatTime = (isoTime) => {
        if (!isoTime) return '--:--';
        try {
            return new Date(isoTime).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        } catch {
            return '--:--';
        }
    };

    if (compact) {
        // Version compacte pour le dashboard
        return (
            <Card className="border-0 shadow-md">
                <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Users className="w-4 h-4 text-emerald-600" />
                            Présences du jour
                        </CardTitle>
                        <Button variant="ghost" size="sm" onClick={fetchPresenceData} disabled={loading}>
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        </Button>
                    </div>
                </CardHeader>
                <CardContent className="pt-0">
                    {/* Stats rapides */}
                    <div className="grid grid-cols-4 gap-2 mb-4">
                        <div className="text-center p-2 rounded-lg bg-blue-50">
                            <div className="text-2xl font-bold text-blue-700">{data?.stats?.total_scheduled || 0}</div>
                            <div className="text-[10px] text-blue-600">Prévus</div>
                        </div>
                        <div className="text-center p-2 rounded-lg bg-green-50">
                            <div className="text-2xl font-bold text-green-700">{data?.stats?.present || 0}</div>
                            <div className="text-[10px] text-green-600">Présents</div>
                        </div>
                        <div className="text-center p-2 rounded-lg bg-amber-50">
                            <div className="text-2xl font-bold text-amber-700">{data?.stats?.late || 0}</div>
                            <div className="text-[10px] text-amber-600">Retards</div>
                        </div>
                        <div className="text-center p-2 rounded-lg bg-red-50">
                            <div className="text-2xl font-bold text-red-700">{data?.stats?.absent || 0}</div>
                            <div className="text-[10px] text-red-600">Absents</div>
                        </div>
                    </div>
                    
                    {/* Liste rapide des présents/retards */}
                    <ScrollArea className="h-[150px]">
                        <div className="space-y-1">
                            {data?.presence_list
                                ?.filter(p => p.status === 'PRESENT' || p.status === 'LATE')
                                .slice(0, 8)
                                .map(p => {
                                    const statusCfg = getStatusConfig(p.status);
                                    const serviceCfg = getServiceConfig(p.service);
                                    return (
                                        <div key={p.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/30 text-sm">
                                            <div className="flex items-center gap-2">
                                                <div className={`w-2 h-2 rounded-full ${statusCfg.color}`} />
                                                <span className="font-medium truncate max-w-[120px]">{p.staff_name}</span>
                                                <Badge variant="outline" className={`text-[9px] ${serviceCfg.bg} ${serviceCfg.text}`}>
                                                    {p.service}
                                                </Badge>
                                            </div>
                                            <span className="text-xs text-muted-foreground">
                                                {formatTime(p.check_in_time)}
                                            </span>
                                        </div>
                                    );
                                })}
                            {data?.presence_list?.filter(p => p.status === 'PRESENT' || p.status === 'LATE').length === 0 && (
                                <div className="text-center py-4 text-muted-foreground text-sm">
                                    Aucun employé présent pour le moment
                                </div>
                            )}
                        </div>
                    </ScrollArea>
                    
                    <Link to="/attendance">
                        <Button variant="outline" size="sm" className="w-full mt-3">
                            Voir toutes les présences
                        </Button>
                    </Link>
                </CardContent>
            </Card>
        );
    }

    // Version complète
    return (
        <Card className="border-0 shadow-md">
            <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-emerald-600" />
                        Présences en temps réel
                    </CardTitle>
                    <div className="flex items-center gap-2">
                        <Select value={serviceFilter} onValueChange={setServiceFilter}>
                            <SelectTrigger className="w-32 h-8 text-xs">
                                <Filter className="w-3 h-3 mr-1" />
                                <SelectValue placeholder="Service" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous</SelectItem>
                                {data?.services?.map(s => (
                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-32 h-8 text-xs">
                                <SelectValue placeholder="Statut" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous</SelectItem>
                                <SelectItem value="PRESENT">Présent</SelectItem>
                                <SelectItem value="LATE">En retard</SelectItem>
                                <SelectItem value="PENDING">En attente</SelectItem>
                                <SelectItem value="ABSENT">Absent</SelectItem>
                            </SelectContent>
                        </Select>
                        <Button variant="outline" size="sm" onClick={fetchPresenceData} disabled={loading}>
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                    <div className="text-center p-3 rounded-xl bg-blue-50 border border-blue-100">
                        <div className="text-3xl font-bold text-blue-700">{data?.stats?.total_scheduled || 0}</div>
                        <div className="text-xs text-blue-600 font-medium">Total prévus</div>
                    </div>
                    <div className="text-center p-3 rounded-xl bg-green-50 border border-green-100">
                        <CheckCircle2 className="w-5 h-5 mx-auto text-green-600 mb-1" />
                        <div className="text-3xl font-bold text-green-700">{data?.stats?.present || 0}</div>
                        <div className="text-xs text-green-600 font-medium">Présents</div>
                    </div>
                    <div className="text-center p-3 rounded-xl bg-amber-50 border border-amber-100">
                        <Clock className="w-5 h-5 mx-auto text-amber-600 mb-1" />
                        <div className="text-3xl font-bold text-amber-700">{data?.stats?.late || 0}</div>
                        <div className="text-xs text-amber-600 font-medium">En retard</div>
                    </div>
                    <div className="text-center p-3 rounded-xl bg-gray-50 border border-gray-200">
                        <AlertTriangle className="w-5 h-5 mx-auto text-gray-500 mb-1" />
                        <div className="text-3xl font-bold text-gray-700">{data?.stats?.pending || 0}</div>
                        <div className="text-xs text-gray-600 font-medium">En attente</div>
                    </div>
                    <div className="text-center p-3 rounded-xl bg-red-50 border border-red-100">
                        <XCircle className="w-5 h-5 mx-auto text-red-600 mb-1" />
                        <div className="text-3xl font-bold text-red-700">{data?.stats?.absent || 0}</div>
                        <div className="text-xs text-red-600 font-medium">Absents</div>
                    </div>
                </div>
                
                {/* Par service */}
                {data?.services?.length > 1 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6">
                        {data.services.map(service => {
                            const svc = data.stats.by_service[service];
                            const cfg = getServiceConfig(service);
                            const ServiceIcon = cfg.icon;
                            return (
                                <div key={service} className={`p-3 rounded-lg ${cfg.bg} border`}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <ServiceIcon className={`w-4 h-4 ${cfg.text}`} />
                                        <span className={`font-medium text-sm ${cfg.text}`}>{service}</span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-1 text-xs">
                                        <span className="text-green-600">✓ {svc.present + svc.late}</span>
                                        <span className="text-red-600">✗ {svc.absent}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                
                {/* Liste détaillée */}
                <ScrollArea className="h-[300px]">
                    <div className="space-y-2">
                        {filteredList.map(p => {
                            const statusCfg = getStatusConfig(p.status);
                            const serviceCfg = getServiceConfig(p.service);
                            return (
                                <div 
                                    key={p.id} 
                                    className={`flex items-center justify-between p-3 rounded-lg border ${statusCfg.bgLight} ${p.is_closed ? 'opacity-70' : ''}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`w-3 h-3 rounded-full ${statusCfg.color}`} />
                                        <div>
                                            <div className="font-medium">{p.staff_name}</div>
                                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                <Badge variant="outline" className={`${serviceCfg.bg} ${serviceCfg.text} text-[10px]`}>
                                                    {p.service}
                                                </Badge>
                                                <span>{p.start_time} - {p.end_time}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <Badge className={`${statusCfg.color} text-white text-xs`}>
                                            {statusCfg.label}
                                        </Badge>
                                        {p.check_in_time && (
                                            <div className="text-xs text-muted-foreground mt-1">
                                                Arrivé: {formatTime(p.check_in_time)}
                                                {p.late_minutes > 0 && (
                                                    <span className="text-amber-600 ml-1">(+{p.late_minutes}min)</span>
                                                )}
                                            </div>
                                        )}
                                        {p.check_out_time && (
                                            <div className="text-xs text-muted-foreground">
                                                Parti: {formatTime(p.check_out_time)}
                                                {p.auto_clock_out && <span className="text-blue-500 ml-1">(auto)</span>}
                                            </div>
                                        )}
                                        {p.is_closed && (
                                            <div className="text-[10px] text-slate-500 mt-1">🔒 Clôturé</div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        {filteredList.length === 0 && (
                            <div className="text-center py-8 text-muted-foreground">
                                <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                                <p>Aucun shift prévu ou correspondant aux filtres</p>
                            </div>
                        )}
                    </div>
                </ScrollArea>
            </CardContent>
        </Card>
    );
};

export default StaffPresenceModule;
